import { Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';

import { SessionService } from '../auth/session.service';
import { BillingFeatureSnapshot } from './billing-feature.models';

@Injectable({ providedIn: 'root' })
export class BillingFeatureAccessService {
  readonly loading = signal(false);
  readonly snapshot = signal<BillingFeatureSnapshot | null>(null);
  readonly errorMessage = signal<string | null>(null);

  constructor(private readonly session: SessionService) {}

  ensureCurrentCompanyFeatures(force = false): Observable<BillingFeatureSnapshot | null> {
    void force;
    const me = this.session.getCurrentUser();
    const companyId = me?.company_id ?? null;
    if (!companyId) {
      this.reset();
      return of(null);
    }

    const featureCodes = Array.isArray(me?.enabled_features) ? me.enabled_features : [];
    const normalizedCodes = Array.from(
      new Set(
        featureCodes
          .map((item) => this.normalizeFeatureCode(item))
          .filter((item) => item.length > 0),
      ),
    ).sort((a, b) => a.localeCompare(b));

    const snapshot: BillingFeatureSnapshot = {
      company_id: companyId,
      billing_model: 'AUTH_ME',
      plan_code: 'AUTH_ME',
      features: normalizedCodes.map((code) => ({
        feature_code: code,
        enabled: true,
        limit_value: null,
      })),
    };

    this.loading.set(false);
    this.snapshot.set(snapshot);
    this.errorMessage.set(null);
    return of(snapshot);
  }

  prefetchCurrentCompanyFeatures(force = false): void {
    this.ensureCurrentCompanyFeatures(force).subscribe();
  }

  hasFeature(featureCode: string, snapshot: BillingFeatureSnapshot | null = this.snapshot()): boolean {
    const companyId = this.session.getCurrentUser()?.company_id ?? null;
    if (!companyId || !snapshot || snapshot.company_id !== companyId) {
      return false;
    }

    const normalized = this.normalizeFeatureCode(featureCode);
    if (!normalized) {
      return false;
    }

    const found = snapshot.features.find(
      (item) => this.normalizeFeatureCode(item.feature_code) === normalized,
    );
    return Boolean(found?.enabled);
  }

  hasAllFeatures(featureCodes: string[], snapshot: BillingFeatureSnapshot | null = this.snapshot()): boolean {
    return featureCodes.every((code) => this.hasFeature(code, snapshot));
  }

  clear(): void {
    this.reset();
  }

  private reset(): void {
    this.loading.set(false);
    this.snapshot.set(null);
    this.errorMessage.set(null);
  }

  private normalizeFeatureCode(value: string): string {
    return String(value ?? '').trim().toUpperCase();
  }
}
